import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const ExtensionSection = () => {
  const { t } = useTranslation();
  const [browser, setBrowser] = useState("chrome");
  const [showExtensionPopup, setShowExtensionPopup] = useState(false);

  useEffect(() => {
    // Detect user's browser
    const userAgent = navigator.userAgent;
    if (userAgent.includes("Chrome") && !userAgent.includes("Edge")) {
      setBrowser("chrome");
    } else if (userAgent.includes("Firefox")) {
      setBrowser("firefox");
    } else if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) {
      setBrowser("safari");
    } else if (userAgent.includes("Edge")) {
      setBrowser("edge");
    }

    // Show popup animation after delay
    const timer = setTimeout(() => {
      setShowExtensionPopup(true);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const browserData = {
    chrome: {
      name: "Chrome",
      icon: "bi-browser-chrome",
      color: "#4285F4",
      available: true,
      url: "https://chromewebstore.google.com/detail/onbookmarks/mkegjpfabinfdfgiemmfhpfmfeokdbpo",
    },
    edge: {
      name: "Edge",
      icon: "bi-browser-edge",
      color: "#0078D4",
      available: true,
      url: "https://microsoftedge.microsoft.com/addons/detail/gccckckcmaolhemnamcaigcbmaebnodc",
    },
    firefox: {
      name: "Firefox",
      icon: "bi-browser-firefox",
      color: "#FF7139",
      available: true,
      url: "https://addons.mozilla.org/en-US/firefox/addon/onbookmarks/",
    },
    safari: {
      name: "Safari",
      icon: "bi-browser-safari",
      color: "#006CFF",
      available: false,
      comingSoon: "Q3 2025",
    },
  };

  const currentBrowser = browserData[browser] || browserData.chrome;

  return (
    <section className="py-5 overflow-hidden">
      <div className="container">
        <div className="row align-items-center g-5">
          {/* Left Content */}
          <div className="col-lg-6">
            <h2 className="display-5 fw-bold mb-4">
              {t("extensionSection.title")} <br />
              <span className="text-primary">
                {t("extensionSection.titleHighlight")}
              </span>
            </h2>

            <p className="lead text-muted mb-4">
              {t("extensionSection.description")}
            </p>

            {/* Feature List */}
            <div className="d-flex flex-column gap-3 mb-5">
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-check-circle-fill text-success"></i>
                <span className="text-muted">
                  {t("extensionSection.features.instantSave")}
                </span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-check-circle-fill text-success"></i>
                <span className="text-muted">
                  {t("extensionSection.features.aiTags")}
                </span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-check-circle-fill text-success"></i>
                <span className="text-muted">
                  {t("extensionSection.features.sync")}
                </span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="d-flex gap-3 flex-wrap">
              {currentBrowser.available ? (
                <Link
                  to={currentBrowser.url}
                  className="btn bg-purple px-4 d-inline-flex align-items-center gap-2 text-white"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className={`bi ${currentBrowser.icon} me-1`}></i>
                  <span>
                    {t("extensionSection.buttons.addToBrowser", {
                      browserName: currentBrowser.name,
                    })}
                  </span>
                </Link>
              ) : (
                <button className="btn btn-secondary px-4 disabled" disabled>
                  <i className={`bi ${currentBrowser.icon} me-1`}></i>
                  <span>
                    {t("extensionSection.buttons.comingSoon", {
                      browserName: currentBrowser.name,
                      date: currentBrowser.comingSoon,
                    })}
                  </span>
                </button>
              )}
              <Link to="/download/extensions" className="btn btn-white px-4">
                {t("extensionSection.buttons.allBrowsers")}
              </Link>
            </div>

            {/* Trust Badge */}
            <div className="mt-4">
              <div className="d-flex align-items-center gap-4 text-muted small">
                <span>
                  <i className="bi bi-shield-check me-1"></i>
                  {t("extensionSection.trust.noHistory")}
                </span>
              </div>
            </div>
          </div>

          {/* Right Visual */}
          <div className="col-lg-6">
            <div style={{ position: "relative", perspective: "1000px" }}>
              {/* Browser Window */}
              <div
                style={{
                  background: "white",
                  borderRadius: "12px",
                  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
                  overflow: "hidden",
                  transform: "rotateY(-5deg)",
                  transition: "transform 0.3s ease",
                }}
              >
                {/* Browser Chrome */}
                <div
                  style={{
                    background: "#f3f4f6",
                    padding: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <div style={{ display: "flex", gap: "6px" }}>
                    <div
                      style={{
                        width: "12px",
                        height: "12px",
                        borderRadius: "50%",
                        background: "#ef4444",
                      }}
                    ></div>
                    <div
                      style={{
                        width: "12px",
                        height: "12px",
                        borderRadius: "50%",
                        background: "#f59e0b",
                      }}
                    ></div>
                    <div
                      style={{
                        width: "12px",
                        height: "12px",
                        borderRadius: "50%",
                        background: "#22c55e",
                      }}
                    ></div>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      background: "white",
                      borderRadius: "6px",
                      padding: "6px 12px",
                      fontSize: "12px",
                      color: "#6b7280",
                    }}
                  >
                    {t("extensionSection.example.url")}
                  </div>
                  <div
                    className="d-flex align-items-center justify-content-center rounded-3"
                    style={{
                      width: "32px",
                      height: "32px",
                      animation: "pulse 2s infinite",
                    }}
                  >
                    <img
                      src="/images/logo/onbookmarks-icon.png"
                      alt="onBookmarks logo"
                      width={32}
                      height={32}
                      loading="lazy"
                      decoding="async"
                      fetchpriority="low"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                </div>

                {/* Page Content */}
                <div
                  style={{
                    padding: "24px",
                    background: "#fafafa",
                    height: "300px",
                  }}
                >
                  <div
                    style={{
                      background: "#e5e7eb",
                      height: "20px",
                      borderRadius: "4px",
                      marginBottom: "12px",
                      width: "60%",
                    }}
                  ></div>
                  <div
                    style={{
                      background: "#e5e7eb",
                      height: "60px",
                      borderRadius: "4px",
                      marginBottom: "12px",
                    }}
                  ></div>
                  <div
                    style={{
                      background: "#e5e7eb",
                      height: "100px",
                      borderRadius: "4px",
                    }}
                  ></div>
                </div>
              </div>

              {/* Extension Popup */}
              <div
                style={{
                  position: "absolute",
                  top: "60px",
                  right: "-40px",
                  background: "white",
                  borderRadius: "12px",
                  padding: "20px",
                  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
                  minWidth: "280px",
                  transform: showExtensionPopup
                    ? "scale(1) translateY(0)"
                    : "scale(0.8) translateY(-20px)",
                  opacity: showExtensionPopup ? 1 : 0,
                  transition: "all 0.5s ease-out",
                }}
              >
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <span className="fw-semibold">
                    {t("extensionSection.popup.saved")}
                  </span>
                  <i className="bi bi-check-circle-fill text-success"></i>
                </div>

                <div className="mb-3">
                  <small className="text-muted d-block mb-2">
                    {t("extensionSection.popup.autoTagged")}
                  </small>
                  <div className="d-flex gap-2 flex-wrap">
                    {t("extensionSection.example.tags", {
                      returnObjects: true,
                    }).map((tag, index) => (
                      <span key={index} className="badge bg-light text-dark">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mb-3">
                  <small className="text-muted d-block mb-2">
                    {t("extensionSection.popup.addedToCollections")}
                  </small>
                  <div className="d-flex gap-2 flex-wrap">
                    {t("extensionSection.example.collections", {
                      returnObjects: true,
                    }).map((collection, index) => (
                      <span
                        key={index}
                        className="badge bg-primary bg-opacity-10 text-primary"
                      >
                        {collection}
                      </span>
                    ))}
                  </div>
                </div>

                <button className="btn btn-sm btn-primary w-100">
                  {t("extensionSection.popup.viewButton")}
                </button>
              </div>

              {/* Floating Elements */}
              <div
                style={{
                  position: "absolute",
                  top: "20px",
                  left: "-60px",
                  width: "40px",
                  height: "40px",
                  background:
                    "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  animation: "float 3s ease-in-out infinite",
                }}
              >
                <i className="bi bi-lightning-fill"></i>
              </div>

              <div
                style={{
                  position: "absolute",
                  bottom: "40px",
                  right: "-80px",
                  width: "40px",
                  height: "40px",
                  background:
                    "linear-gradient(135deg, #34d399 0%, #22c55e 100%)",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  animation: "float 3s ease-in-out infinite 1s",
                }}
              >
                <i className="bi bi-check-lg"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.4);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(139, 92, 246, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(139, 92, 246, 0);
          }
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0) rotate(0deg);
          }
          50% {
            transform: translateY(-10px) rotate(10deg);
          }
        }
      `}</style>
    </section>
  );
};

export default ExtensionSection;
